import { PageSEO, pageSEO } from '../components/PageSEO';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useAuthPrompt } from '../App';
import { HelpTooltip, HelpContent } from '../components/HelpTooltip';
import { 
  getMileageEntries, createMileageEntry, deleteMileageEntry, exportMileagePDF, calculateDistance,
  getFrequentTrips, createFrequentTrip, logFrequentTrip, deleteFrequentTrip,
  getTripSuggestions, dismissSuggestion,
  getContextMileage, createContextMileage
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Car, Plus, FileDown, Trash2, Loader2, MapPin, Navigation, 
  Play, Square, Clock, Route, Crosshair, AlertCircle, 
  MapPinned, History, Star, Bookmark, ArrowRight, RotateCcw, Zap,
  Lightbulb, TrendingUp, X, Check
} from 'lucide-react';

function MileageEntryRow({ entry, onDelete }) {
  const miles = entry.round_trip ? entry.distance * 2 : entry.distance;
  const deduction = miles * (entry.rate_per_mile || 0.70);
  
  return (
    <div className="mileage-row" data-testid="mileage-entry">
      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Date</p>
          <p className="font-medium">{entry.date}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Route</p>
          <p className="font-medium truncate">{entry.start_location} to {entry.end_location}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Miles</p>
          <p className="font-medium">{miles.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Deduction</p>
          <p className="font-semibold text-green-500">${deduction.toFixed(2)}</p>
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onDelete(entry.id)} data-testid="delete-mileage-button">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

function FrequentTripCard({ trip, onLog, onDelete, logging }) {
  const miles = trip.is_round_trip ? trip.distance * 2 : trip.distance;
  const deduction = miles * 0.70; // 2026 IRS rate
  
  return (
    <div className="p-4 rounded-lg border border-border bg-card hover:border-[hsl(var(--accent))]/50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Star className="w-4 h-4 text-yellow-500 shrink-0" />
            <h4 className="font-semibold truncate">{trip.name}</h4>
            {trip.is_round_trip && (
              <Badge variant="outline" className="text-xs shrink-0">
                <RotateCcw className="w-3 h-3 mr-1" />
                Round Trip
              </Badge>
            )}
          </div>
          <div className="flex items-center text-sm text-muted-foreground gap-1 mb-2">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{trip.start_location}</span>
            <ArrowRight className="w-3 h-3 shrink-0" />
            <span className="truncate">{trip.end_location}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span>{miles.toFixed(1)} mi</span>
            <span className="text-green-500 font-medium">${deduction.toFixed(2)}</span>
            {trip.use_count > 0 && (
              <span className="text-muted-foreground">Used {trip.use_count}x</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button 
            size="sm" 
            className="btn-primary"
            onClick={() => onLog(trip.id)}
            disabled={logging === trip.id}
            data-testid={`log-trip-${trip.id}`}
          >
            {logging === trip.id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Zap className="w-4 h-4" />
            )}
            <span className="ml-1">Log</span>
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => onDelete(trip.id)}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SuggestionCard({ suggestion, onSave, onDismiss, saving }) {
  return (
    <div className="p-4 rounded-lg border border-dashed border-yellow-500/50 bg-yellow-500/5 hover:bg-yellow-500/10 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb className="w-4 h-4 text-yellow-500 shrink-0" />
            <h4 className="font-semibold truncate">{suggestion.suggested_name}</h4>
            <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-500/50 shrink-0">
              {suggestion.trip_count}x driven
            </Badge>
          </div>
          <div className="flex items-center text-sm text-muted-foreground gap-1 mb-2">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="truncate">{suggestion.start_location}</span>
            <ArrowRight className="w-3 h-3 shrink-0" />
            <span className="truncate">{suggestion.end_location}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span>{suggestion.average_distance} mi avg</span>
            <span className="text-green-500 font-medium flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              ${suggestion.potential_annual_deduction}/yr potential
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button 
            size="sm" 
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
            onClick={() => onSave(suggestion)}
            disabled={saving === suggestion.suggested_name}
            data-testid={`save-suggestion-${suggestion.suggested_name}`}
          >
            {saving === suggestion.suggested_name ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            <span className="ml-1">Save</span>
          </Button>
          <Button 
            size="sm" 
            variant="ghost"
            onClick={() => onDismiss(suggestion)}
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function GPSTracker({ onTripComplete }) {
  const [isTracking, setIsTracking] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [startPosition, setStartPosition] = useState(null);
  const [tripDistance, setTripDistance] = useState(0);
  const [tripDuration, setTripDuration] = useState(0);
  const [positions, setPositions] = useState([]);
  const [error, setError] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt');
  
  const watchIdRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setLocationPermission(result.state);
        result.onchange = () => setLocationPermission(result.state);
      });
    }
    
    return () => {
      if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const calculateHaversineDistance = useCallback((lat1, lon1, lat2, lon2) => {
    const R = 3959;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }, []);

  const reverseGeocode = useCallback(async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await response.json();
      if (data.display_name) {
        const parts = data.display_name.split(',').slice(0, 3);
        return parts.join(',').trim();
      }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  }, []);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setError(null);
    setIsTracking(true);
    setTripDistance(0);
    setTripDuration(0);
    setPositions([]);
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      setTripDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          timestamp: Date.now(),
          accuracy: position.coords.accuracy
        };
        setStartPosition(pos);
        setCurrentPosition(pos);
        setPositions([pos]);
        toast.success('GPS tracking started');
      },
      (err) => {
        setError(getGeolocationError(err));
        setIsTracking(false);
        if (timerRef.current) clearInterval(timerRef.current);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const newPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          timestamp: Date.now(),
          accuracy: position.coords.accuracy
        };
        
        setCurrentPosition(newPos);
        setPositions(prev => {
          const updated = [...prev, newPos];
          if (updated.length >= 2) {
            let total = 0;
            for (let i = 1; i < updated.length; i++) {
              if (updated[i].accuracy < 50 && updated[i-1].accuracy < 50) {
                total += calculateHaversineDistance(
                  updated[i-1].lat, updated[i-1].lng,
                  updated[i].lat, updated[i].lng
                );
              }
            }
            setTripDistance(total);
          }
          return updated;
        });
      },
      (err) => console.error('Watch position error:', err),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  }, [calculateHaversineDistance]);

  const stopTracking = useCallback(async () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsTracking(false);

    if (positions.length < 2) {
      toast.error('Trip too short to record');
      return;
    }

    const startAddr = await reverseGeocode(positions[0].lat, positions[0].lng);
    const endAddr = await reverseGeocode(positions[positions.length - 1].lat, positions[positions.length - 1].lng);

    onTripComplete({
      startLocation: startAddr,
      endLocation: endAddr,
      distance: tripDistance,
      duration: tripDuration,
      positions: positions
    });

    setStartPosition(null);
    setCurrentPosition(null);
    setPositions([]);
    setTripDistance(0);
    setTripDuration(0);
  }, [positions, tripDistance, tripDuration, reverseGeocode, onTripComplete]);

  const getGeolocationError = (error) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'Location permission denied. Please enable location access.';
      case error.POSITION_UNAVAILABLE:
        return 'Location information unavailable.';
      case error.TIMEOUT:
        return 'Location request timed out.';
      default:
        return 'An unknown error occurred.';
    }
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`;
    return `${mins}m ${secs}s`;
  };

  if (locationPermission === 'denied') {
    return (
      <Card className="border-orange-500/50 bg-orange-500/5">
        <CardContent className="p-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-orange-500 mb-4" />
          <h3 className="font-semibold text-lg mb-2">Location Access Required</h3>
          <p className="text-muted-foreground mb-4">
            GPS tracking requires location permission. Please enable location access in your browser settings.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isTracking ? 'border-green-500/50 bg-green-500/5' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Crosshair className={`w-5 h-5 ${isTracking ? 'text-green-500 animate-pulse' : 'text-muted-foreground'}`} />
          GPS Auto-Track
          {isTracking && <Badge variant="outline" className="text-green-500 border-green-500">Recording</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4 inline mr-2" />{error}
          </div>
        )}

        {isTracking ? (
          <>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-muted/50">
                <Route className="w-4 h-4 mx-auto mb-1 text-[hsl(var(--accent))]" />
                <p className="text-xl font-bold">{tripDistance.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Miles</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <Clock className="w-4 h-4 mx-auto mb-1 text-[hsl(var(--accent))]" />
                <p className="text-xl font-bold">{formatDuration(tripDuration)}</p>
                <p className="text-xs text-muted-foreground">Duration</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50">
                <MapPinned className="w-4 h-4 mx-auto mb-1 text-[hsl(var(--accent))]" />
                <p className="text-xl font-bold">{positions.length}</p>
                <p className="text-xs text-muted-foreground">Points</p>
              </div>
            </div>
            <Button className="w-full bg-red-600 hover:bg-red-700" onClick={stopTracking} data-testid="stop-tracking-button">
              <Square className="w-4 h-4 mr-2" />Stop & Save Trip
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground text-center">
              Start tracking to automatically record your trip using GPS.
            </p>
            <Button className="w-full btn-primary" onClick={startTracking} data-testid="start-tracking-button">
              <Play className="w-4 h-4 mr-2" />Start GPS Tracking
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function Mileage({ context }) {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  const location = useLocation();
  
  // Determine context from prop or URL
  const dataContext = context || (location.pathname.startsWith('/personal') ? 'personal' : 'business');
  
  const [entries, setEntries] = useState([]);
  const [totalMiles, setTotalMiles] = useState(0);
  const [totalDeduction, setTotalDeduction] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saveAsFrequentOpen, setSaveAsFrequentOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [formDate, setFormDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [formStart, setFormStart] = useState('');
  const [formEnd, setFormEnd] = useState('');
  const [formDistance, setFormDistance] = useState('');
  const [formPurpose, setFormPurpose] = useState('Client Meeting');
  const [formRoundTrip, setFormRoundTrip] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  
  // Frequent trips state
  const [frequentTrips, setFrequentTrips] = useState([]);
  const [loggingTrip, setLoggingTrip] = useState(null);
  const [frequentTripName, setFrequentTripName] = useState('');
  const [savingFrequent, setSavingFrequent] = useState(false);
  
  // Smart suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [savingSuggestion, setSavingSuggestion] = useState(null);
  
  const year = new Date().getFullYear();
  const rate = 0.70; // 2026 IRS standard mileage rate
  
  // Auth-protected action wrapper
  const requireAuth = (action, feature) => {
    if (!isAuthenticated) {
      showAuthPrompt(feature);
      return false;
    }
    action();
    return true;
  };

  async function fetchData() {
    try {
      setLoading(true);
      // Use context-aware API for mileage
      const [mileageRes, tripsRes, suggestionsRes] = await Promise.all([
        getContextMileage(dataContext, { year }),
        getFrequentTrips(),
        getTripSuggestions()
      ]);
      setEntries(mileageRes.data.entries || []);
      setTotalMiles(mileageRes.data.total_distance || mileageRes.data.total_miles || 0);
      setTotalDeduction(mileageRes.data.total_deduction || 0);
      setFrequentTrips(tripsRes.data || []);
      setSuggestions(suggestionsRes.data.suggestions || []);
    } catch (error) {
      toast.error('Failed to load mileage data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [dataContext]);

  async function autoCalculateDistance() {
    if (!formStart || !formEnd) {
      toast.error('Please enter both start and end addresses');
      return;
    }
    
    setCalculatingDistance(true);
    try {
      const res = await calculateDistance(formStart, formEnd);
      if (res.data.success) {
        setFormDistance(res.data.estimated_driving_distance.toString());
        toast.success(`Distance calculated: ${res.data.estimated_driving_distance} miles`);
      } else {
        toast.error(res.data.error || 'Could not calculate distance');
      }
    } catch (error) {
      toast.error('Failed to calculate distance');
    } finally {
      setCalculatingDistance(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formDate || !formStart || !formEnd || !formDistance || !formPurpose) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await createMileageEntry({
        date: formDate,
        start_location: formStart,
        end_location: formEnd,
        distance: parseFloat(formDistance),
        purpose: formPurpose,
        round_trip: formRoundTrip
      });
      toast.success('Mileage entry added');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  }

  function resetForm() {
    setFormStart('');
    setFormEnd('');
    setFormDistance('');
    setFormRoundTrip(false);
    setFormDate(format(new Date(), 'yyyy-MM-dd'));
  }

  async function handleSaveAsFrequent() {
    if (!frequentTripName || !formStart || !formEnd || !formDistance) {
      toast.error('Please fill in trip details first');
      return;
    }

    setSavingFrequent(true);
    try {
      await createFrequentTrip({
        name: frequentTripName,
        start_location: formStart,
        end_location: formEnd,
        distance: parseFloat(formDistance),
        purpose: formPurpose,
        is_round_trip: formRoundTrip
      });
      toast.success('Trip saved to Frequent Trips!');
      setSaveAsFrequentOpen(false);
      setFrequentTripName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to save frequent trip');
    } finally {
      setSavingFrequent(false);
    }
  }

  async function handleLogFrequentTrip(tripId) {
    setLoggingTrip(tripId);
    try {
      const res = await logFrequentTrip(tripId);
      toast.success(`Trip logged! Deduction: $${res.data.deduction.toFixed(2)}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to log trip');
    } finally {
      setLoggingTrip(null);
    }
  }

  async function handleSaveSuggestion(suggestion) {
    setSavingSuggestion(suggestion.suggested_name);
    try {
      await createFrequentTrip({
        name: suggestion.suggested_name,
        start_location: suggestion.start_location,
        end_location: suggestion.end_location,
        distance: suggestion.average_distance,
        purpose: suggestion.suggested_purpose,
        is_round_trip: false
      });
      toast.success(`"${suggestion.suggested_name}" saved to Frequent Trips!`);
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.suggested_name !== suggestion.suggested_name));
      fetchData();
    } catch (error) {
      toast.error('Failed to save trip');
    } finally {
      setSavingSuggestion(null);
    }
  }

  async function handleDismissSuggestion(suggestion) {
    try {
      await dismissSuggestion(suggestion.start_location, suggestion.end_location);
      setSuggestions(prev => prev.filter(s => s.suggested_name !== suggestion.suggested_name));
      toast.info('Suggestion dismissed');
    } catch (error) {
      // Still remove from UI even if API fails
      setSuggestions(prev => prev.filter(s => s.suggested_name !== suggestion.suggested_name));
    }
  }

  async function handleDeleteFrequentTrip(tripId) {
    try {
      await deleteFrequentTrip(tripId);
      toast.success('Frequent trip removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete trip');
    }
  }

  async function handleGPSTripComplete(tripData) {
    setDialogOpen(true);
    setFormStart(tripData.startLocation);
    setFormEnd(tripData.endLocation);
    setFormDistance(tripData.distance.toFixed(2));
    setFormDate(format(new Date(), 'yyyy-MM-dd'));
    toast.success(`Trip recorded: ${tripData.distance.toFixed(2)} miles`);
  }

  async function handleDelete(id) {
    try {
      await deleteMileageEntry(id);
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  }

  async function handleExportPDF() {
    setExporting(true);
    try {
      const res = await exportMileagePDF(year);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mileage_log.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="skeleton h-32 rounded-lg mb-4" />
        <div className="skeleton h-64 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <PageSEO {...pageSEO.mileage} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Mileage</h1>
          <p className="text-muted-foreground">Track business miles for tax deduction</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="btn-primary" 
                data-testid="add-mileage-button"
                onClick={(e) => {
                  if (!isAuthenticated) {
                    e.preventDefault();
                    showAuthPrompt('log mileage');
                  }
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Log Trip
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Log Mileage</DialogTitle>
                <DialogDescription>Enter your trip details to calculate mileage deduction</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} data-testid="mileage-date-input" />
                </div>
                <div>
                  <Label>Start Location</Label>
                  <Input placeholder="Starting address" value={formStart} onChange={(e) => setFormStart(e.target.value)} required data-testid="mileage-start-input" />
                </div>
                <div>
                  <Label>End Location</Label>
                  <Input placeholder="Destination address" value={formEnd} onChange={(e) => setFormEnd(e.target.value)} required data-testid="mileage-end-input" />
                </div>
                <div>
                  <Label>Distance (miles)</Label>
                  <div className="flex gap-2">
                    <Input type="number" step="0.1" min="0" placeholder="0.0" value={formDistance} onChange={(e) => setFormDistance(e.target.value)} required data-testid="mileage-distance-input" className="flex-1" />
                    <Button type="button" variant="outline" onClick={autoCalculateDistance} disabled={calculatingDistance || !formStart || !formEnd} data-testid="auto-calculate-distance-button">
                      {calculatingDistance ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Purpose</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formPurpose} onChange={(e) => setFormPurpose(e.target.value)} data-testid="mileage-purpose-select">
                    <option value="Client Meeting">Client Meeting</option>
                    <option value="Site Visit">Site Visit</option>
                    <option value="Supply Run">Supply Run</option>
                    <option value="Business Errand">Business Errand</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="round-trip">Round Trip</Label>
                  <Switch id="round-trip" checked={formRoundTrip} onCheckedChange={setFormRoundTrip} data-testid="round-trip-switch" />
                </div>
                <div className="flex justify-between gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setSaveAsFrequentOpen(true)} disabled={!formStart || !formEnd || !formDistance}>
                    <Star className="w-4 h-4 mr-2" /> Save as Frequent
                  </Button>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="btn-primary" disabled={submitting} data-testid="mileage-submit-button">
                      {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Add Entry
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Button 
            variant="outline" 
            onClick={() => requireAuth(handleExportPDF, 'export mileage data')} 
            disabled={exporting || entries.length === 0} 
            data-testid="mileage-export-pdf-button"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
            Export PDF
          </Button>
        </div>
      </div>

      {/* Save as Frequent Dialog */}
      <Dialog open={saveAsFrequentOpen} onOpenChange={setSaveAsFrequentOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save as Frequent Trip</DialogTitle>
            <DialogDescription>Give this trip a name for quick one-tap logging</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Trip Name</Label>
              <Input 
                placeholder="e.g., Home to Office" 
                value={frequentTripName} 
                onChange={(e) => setFrequentTripName(e.target.value)}
                data-testid="frequent-trip-name-input"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p><strong>From:</strong> {formStart}</p>
              <p><strong>To:</strong> {formEnd}</p>
              <p><strong>Distance:</strong> {formDistance} miles {formRoundTrip && '(round trip)'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAsFrequentOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={handleSaveAsFrequent} disabled={savingFrequent || !frequentTripName}>
              {savingFrequent && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Summary Card */}
      <Card className="g-hero">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                Total Miles ({year})
                <HelpTooltip content={HelpContent.mileage.businessMileage} />
              </p>
              <p className="text-3xl font-bold">{totalMiles.toFixed(1)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                IRS Rate
                <HelpTooltip content={HelpContent.mileage.irsRate} />
              </p>
              <p className="text-3xl font-bold">${rate}/mi</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Deduction</p>
              <p className="text-3xl font-bold text-green-500">${totalDeduction.toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Frequent Trips */}
      {frequentTrips.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Star className="w-5 h-5 text-yellow-500" />
              Frequent Trips
              <HelpTooltip content={HelpContent.mileage.frequentTrips} />
              <Badge variant="outline" className="ml-auto">{frequentTrips.length} saved</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {frequentTrips.map((trip) => (
                <FrequentTripCard 
                  key={trip.id} 
                  trip={trip} 
                  onLog={handleLogFrequentTrip}
                  onDelete={handleDeleteFrequentTrip}
                  logging={loggingTrip}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Trip Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-yellow-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Smart Suggestions
              <Badge variant="outline" className="text-yellow-600 border-yellow-500/50 ml-auto">
                {suggestions.length} patterns found
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              We noticed you drive these routes often. Save them for one-tap logging!
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {suggestions.map((suggestion, idx) => (
                <SuggestionCard 
                  key={idx}
                  suggestion={suggestion}
                  onSave={handleSaveSuggestion}
                  onDismiss={handleDismissSuggestion}
                  saving={savingSuggestion}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* GPS Tracker */}
      <GPSTracker onTripComplete={handleGPSTripComplete} />

      {/* Trip Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Trip Log
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {entries.length === 0 ? (
            <div className="empty-state py-8">
              <Car className="empty-state-icon" />
              <h3 className="text-lg font-semibold mb-2">No trips logged</h3>
              <p className="text-muted-foreground mb-4">Start logging your business miles using GPS tracking, frequent trips, or manual entry</p>
            </div>
          ) : (
            entries.map((entry) => (
              <MileageEntryRow key={entry.id} entry={entry} onDelete={handleDelete} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
